/**
 * DispatchComponent.ts
 * @author Diao Zheng
 * @file Component that handles dispatchers
 * @barrel export DispatchComponentLifecycleActions
 * @barrel export IDispatchable
 * @barrel export IDispatchableWithLegacyEventHandlers
 * @barrel export RestrictDispatch
 * @barrel export ExcludeDispatch
 */
import { invariant, Option, } from "nasi";
import React from "react";
import { Dispatcher } from "./Dispatcher";
import * as Intent from "./Intent";
import * as Stable from "./Stable";
function isLifecycleAction(action) {
    return (action &&
        typeof action === "object" &&
        (action.action === "WILL_MOUNT" || action.action === "DID_UPDATE"));
}
export class DispatchComponent extends React.Component {
    constructor(props) {
        super(props);
        this.send = Stable.declareAsStable((action) => {
            this.internalDispatcher.dispatch(action);
        });
        this.currentSideEffectQueue = [];
        this.doReduce = (action) => {
            const intent = (this.reducer ?
                this.reducer(this.state, action)
                : this.actionOnlyReducer ?
                    this.actionOnlyReducer(action)
                    :
                        null);
            invariant(() => intent !== undefined, "Reducer should not return `undefined`. Is your reducer total?", false);
            invariant(() => !(this.reducer && this.actionOnlyReducer), "Should both `reducer` and `actionOnlyReducer` be defined for " +
                "DispatchComponent, we will only use `reducer`. `actionOnlyReducer` " +
                "will never be called.", false);
            if (intent) {
                if (intent.update) {
                    if (intent.effect) {
                        this.currentSideEffectQueue.push(...intent.effect);
                    }
                    this.setState(intent.update);
                }
                else if (intent.effect) {
                    return this.flushSideEffects(intent.effect, this.state);
                }
            }
            this.internalLifecycleHandler(action);
        };
        this.dispatcher = new Dispatcher(this.constructor.name);
        this.internalDispatcher = new Dispatcher(this.constructor.name + "_internalDispatcher");
        this.currentInternalDispatchIdentifier = this.internalDispatcher.add(this.doReduce);
        this.currentExternalDispatchIdentifier = this.dispatcher.add(Option.value(props.dispatch, this.fireLegacyEventListeners.bind(this)));
    }
    componentWillUnmount() {
        this.dispatcher.remove(this.currentExternalDispatchIdentifier);
        this.internalDispatcher.dispatch({ action: "WILL_UNMOUNT" });
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        this.internalDispatcher.dispatch({
            action: "DID_UPDATE",
            payload: {
                prevProps,
                prevState,
                snapshot,
            },
        });
    }
    ignoreLifecycleActions(action) {
        switch (action.action) {
            case "WILL_UNMOUNT":
                return Intent.NoUpdate();
            case "DID_UPDATE":
                return Intent.NoUpdate();
            default:
                invariant(() => false, "`DispatchAction.ignoreLifecycleActions` should be placed in the " +
                    "`default` case in a switch statement in your reducer, and your " +
                    "reducer should be exhaustive.");
                return Intent.NoUpdate();
        }
    }
    // tslint:enable:variable-name
    fireLegacyEventListeners(_) {
        return;
    }
    dispatch(action) {
        return this.dispatcher.dispatch.bind(this.dispatcher, action);
    }
    internalLifecycleHandler(action) {
        if (!isLifecycleAction(action)) {
            return;
        }
        switch (action.action) {
            case "DID_UPDATE":
                if (action.payload.prevProps.dispatch !== this.props.dispatch) {
                    this.dispatcher.remove(this.currentExternalDispatchIdentifier);
                    this.currentExternalDispatchIdentifier = this.dispatcher.add(Option.value(this.props.dispatch, this.fireLegacyEventListeners.bind(this)));
                }
                this.flushSideEffects(this.currentSideEffectQueue, action.payload.prevState);
                break;
            case "WILL_UNMOUNT":
                this.internalDispatcher.remove(this.currentInternalDispatchIdentifier);
                break;
        }
    }
    flushSideEffects(effectQueue, state) {
        while (effectQueue.length) {
            const effect = effectQueue.shift();
            if (effect) {
                effect(state);
            }
        }
    }
}
